import { useState } from "react"

import { Checkbox, Divider, Drawer, Flex, Space, Tooltip, Button, Tabs, TabsProps, Segmented, Form } from "antd"
import { CheckboxChangeEvent } from "antd/es/checkbox"
import { Dayjs } from "dayjs"

import variables from "@/_styles/variables.module.scss"
import { SvgImage } from "@/components/_Shared/SvgImage/SvgImage"

import AnalyticsSettings, { AnalyticsSettingsOptions } from "./AnalyticsSettings"
import classes from "./AnalyticsSettings.module.scss"

import { useCustomerClientsSelectListQuery } from "@/hook/CustomerClients/useCustomerClientQuery"
import { useCustomerCategorySelectListQuery } from "@/hook/Dictionaries/Customers/useCustomerCategoryQuery"
import { useDepotSelectListQuery } from "@/hook/Dictionaries/useDepotQuery"
import { useFuelTypeQuery } from "@/hook/Dictionaries/useFuelTypeQuery"

import { BaseDisplayUnit, baseDisplayUnitsMap, IAnalyticsPageDisplayUnit } from "@/types/IAnalytics"
import { DeliveryType } from "@/types/IOrder"
import { Period } from "@/types/IOrderReport"

const deliveryTypes = [
  {
    label: "Все",
    value: DeliveryType.All
  },
  {
    label: "Доставка",
    value: DeliveryType.Delivery
  },
  {
    label: "Самовывоз",
    value: DeliveryType.Pickup
  }
]

export interface useAnalyticsHeaderProps {
  baseDisplayUnits: BaseDisplayUnit[]
  additionalDisplayUnits?: IAnalyticsPageDisplayUnit[]
  deliveryType?: boolean
  deliveryTypeStrict?: boolean
  hideCustomer?: boolean
}

export const useAnalyticsHeader = (props: useAnalyticsHeaderProps) => {
  const { baseDisplayUnits, additionalDisplayUnits, deliveryType: useDeliveryType, hideCustomer } = props
  const displayUnits = [
    ...baseDisplayUnits.map(u => baseDisplayUnitsMap[u]),
    ...(additionalDisplayUnits ?? [])
  ]

  const defaultOptions: AnalyticsSettingsOptions = {
    depotId: undefined,
    fuelTypeId: undefined,
    startDate: undefined,
    endDate: undefined,
    activePeriod: Period.Year,
    customerClientId: undefined,
    customerCategoryId: undefined,
    deliveryType: DeliveryType.All
  }

  const [optionsList, setOptionsList] = useState<AnalyticsSettingsOptions[]>([defaultOptions])
  const [draftOptionsList, setDraftOptionsList] = useState<AnalyticsSettingsOptions[]>([defaultOptions])

  const [compare, setCompare] = useState(false)
  const [draftCompare, setDraftCompare] = useState(false)
  const [displayUnit, setDisplayUnit] = useState<IAnalyticsPageDisplayUnit>(displayUnits[0])
  const [deliveryType, setDeliveryType] = useState<DeliveryType>(DeliveryType.All)
  const [draftDeliveryType, setDraftDeliveryType] = useState<DeliveryType>(DeliveryType.All)

  const onCompareChange = (checked: boolean) => {
    const newOptionsCount = checked ? 2 : 1

    setDraftOptionsList(() => {
      const newOptionsList = draftOptionsList
      if (newOptionsCount === 1) {
        return [newOptionsList[0]]
      } else {
        const oldOptions = draftOptionsList[0]

        for (let i = 1; i < newOptionsCount; i++) {
          newOptionsList.push({
            ...oldOptions
          })
        }
      }

      return newOptionsList
    })
    setDraftCompare(checked)
  }

  const onDeliveryTypeChange = (deliveryType: DeliveryType) => {
    setDraftDeliveryType(deliveryType)
    setDraftOptionsList(prev => {
      const newOptions = [...prev]
      newOptions.forEach(o => o.deliveryType = deliveryType)
      return newOptions
    })
  }

  const getOnRangeChangeFunction = (optionsId: number) => {
    if (optionsId < 1)
      return

    return (dates: [start: Dayjs | null, end: Dayjs | null] | null, dateStrings: [string, string]) => {
      if (!dates || dates.length === 2) {
        setDraftOptionsList(prev => {
          const newOptions = [...prev]
          const thisOptions = newOptions[optionsId - 1]
          thisOptions.startDate = dates ? dates[0]?.format("YYYY-MM-DD") : undefined
          thisOptions.endDate = dates ? dates[1]?.format("YYYY-MM-DD") : undefined
          thisOptions.activePeriod = dates ? undefined : Period.Year
          return newOptions
        })
      }
    }
  }

  const getOnPeriodChangeFunction = (optionsId: number) => {
    if (optionsId < 1)
      return

    return (period: Period) => {
      if (!isNaN(period) && period >= 0) {
        setDraftOptionsList(prev => {
          const newOptions = [...prev]
          const thisOptions = newOptions[optionsId - 1]
          thisOptions.startDate = undefined
          thisOptions.endDate = undefined
          thisOptions.activePeriod = period
          return newOptions
        })
      }
    }
  }

  const getOnFuelTypeChangeFunction = (optionsId: number) => {
    if (optionsId < 1)
      return

    return (fuelTypeId: number | undefined) => {
      setDraftOptionsList(prev => {
        const newOptions = [...prev]
        const thisOptions = newOptions[optionsId - 1]
        thisOptions.fuelTypeId = fuelTypeId
        return newOptions
      })
    }
  }

  const getOnDepotChangeFunction = (optionsId: number) => {
    if (optionsId < 1)
      return

    return (depotId: number | undefined) => {
      setDraftOptionsList(prev => {
        const newOptions = [...prev]
        const thisOptions = newOptions[optionsId - 1]
        thisOptions.depotId = depotId
        return newOptions
      })
    }
  }

  const getOnCustomerClientChangeFunction = (optionsId: number) => {
    if (optionsId < 1)
      return

    return (clientId: number | undefined) => {
      setDraftOptionsList(prev => {
        const newOptions = [...prev]
        const thisOptions = newOptions[optionsId - 1]
        thisOptions.customerCategoryId = undefined
        thisOptions.customerClientId = clientId
        return newOptions
      })
    }
  }

  const getOnCustomerCategoryChangeFunction = (optionsId: number) => {
    if (optionsId < 1)
      return

    return (customerCategoryId: number | undefined) => {
      setDraftOptionsList(prev => {
        const newOptions = [...prev]
        const thisOptions = newOptions[optionsId - 1]
        thisOptions.customerClientId = undefined
        thisOptions.customerCategoryId = customerCategoryId
        return newOptions
      })
    }
  }

  const component =
    <AnalyticsHeader
      onCompareChange={onCompareChange}
      getOnPeriodChangeFunction={getOnPeriodChangeFunction}
      getOnRangeChangeFunction={getOnRangeChangeFunction}
      getOnFuelTypeChangeFunction={getOnFuelTypeChangeFunction}
      getOnDepotChangeFunction={getOnDepotChangeFunction}
      getOnCustomerClientChangeFunction={getOnCustomerClientChangeFunction}
      getOnCustomerCategoryChangeFunction={getOnCustomerCategoryChangeFunction}
      onDeliveryTypeChange={onDeliveryTypeChange}
      onDisplayUnitChange={setDisplayUnit}
      draftOptionsList={draftOptionsList}
      setOptionsList={setOptionsList}
      setDraftOptionsList={setDraftOptionsList}
      setCompare={setCompare}
      setDeliveryType={setDeliveryType}
      setDraftCompare={setDraftCompare}
      setDraftDeliveryType={setDraftDeliveryType}
      optionsList={optionsList}
      displayUnit={displayUnit}
      displayUnits={displayUnits}
      isComparison={compare}
      draftIsComparison={draftCompare}
      useDeliveryType={useDeliveryType}
      deliveryType={draftDeliveryType}
      hideCustomer={hideCustomer}/>

  return {
    optionsList,
    component,
    displayUnit: displayUnit,
    isComparison: compare,
    deliveryType: deliveryType
  }
}

interface CustomerAnalyticsHeaderProps {
  optionsList: AnalyticsSettingsOptions[]
  draftOptionsList: AnalyticsSettingsOptions[]
  setOptionsList: (options: AnalyticsSettingsOptions[]) => void
  setDraftOptionsList: (options: AnalyticsSettingsOptions[]) => void
  setCompare: (compare: boolean) => void
  setDeliveryType: (type: DeliveryType) => void
  setDraftCompare: (compare: boolean) => void
  setDraftDeliveryType: (type: DeliveryType) => void
  onCompareChange: (checked: boolean) => void
  onDeliveryTypeChange?: ((deliveryType: DeliveryType) => void) | undefined
  getOnPeriodChangeFunction: (optionsId: number) => ((period: Period) => void) | undefined
  getOnRangeChangeFunction: (optionsId: number) => ((dates: [start: Dayjs | null, end: Dayjs | null] | null, dateStrings: [string, string]) => void) | undefined
  getOnFuelTypeChangeFunction: (optionsId: number) => ((fuelTypeId: (number | undefined)) => void) | undefined
  getOnDepotChangeFunction: (optionsId: number) => ((depotId: (number | undefined)) => void) | undefined
  getOnCustomerClientChangeFunction: (optionsId: number) => ((customerClientId: (number | undefined)) => void) | undefined
  getOnCustomerCategoryChangeFunction: (optionsId: number) => ((customerCategoryId: (number | undefined)) => void) | undefined
  onDisplayUnitChange: ((newUnit: IAnalyticsPageDisplayUnit) => void) | undefined
  displayUnit: IAnalyticsPageDisplayUnit
  displayUnits: IAnalyticsPageDisplayUnit[]
  isComparison: boolean
  draftIsComparison: boolean
  useDeliveryType?: boolean
  deliveryType?: DeliveryType
  hideCustomer?: boolean
}


const AnalyticsHeader = (props: CustomerAnalyticsHeaderProps) => {
  const {
    optionsList,
    draftOptionsList,
    setOptionsList,
    setDraftOptionsList,
    setCompare,
    setDeliveryType,
    setDraftCompare,
    setDraftDeliveryType,
    onCompareChange,
    getOnPeriodChangeFunction,
    getOnRangeChangeFunction,
    getOnFuelTypeChangeFunction,
    getOnDepotChangeFunction,
    getOnCustomerClientChangeFunction,
    getOnCustomerCategoryChangeFunction,
    onDeliveryTypeChange,
    onDisplayUnitChange,
    isComparison,
    draftIsComparison,
    displayUnit,
    displayUnits,
    useDeliveryType,
    deliveryType,
    hideCustomer
  } = props

  const [openFilters, setOpenFilters] = useState<boolean>(false)
  const { data: clients } = useCustomerClientsSelectListQuery()
  const { data: clientCategories } = useCustomerCategorySelectListQuery()
  const { data: depots } = useDepotSelectListQuery()
  const { data: fuelTypes } = useFuelTypeQuery()

  const onOpenFiltersHandler = () => {
    // Синхронизировать draft состояние с текущим состоянием при открытии
    setDraftOptionsList(optionsList.map(opt => ({ ...opt })))
    setDraftCompare(isComparison)
    setDraftDeliveryType(deliveryType || DeliveryType.All)
    setOpenFilters(true)
  }

  const onCloseFiltersHandler = () => {
    setOpenFilters(false)
  }

  const submitHandler = () => {
    // Применить фильтры: скопировать из draft в основное состояние
    setOptionsList(draftOptionsList.map(opt => ({ ...opt })))
    setCompare(draftIsComparison)
    setDeliveryType(deliveryType || DeliveryType.All)
  }

  const tabsProps: TabsProps["items"] = [
    {
      key: "Base",
      label: "Фильтр"
    },
    {
      key: "Comparison",
      label: "Сравнить"
    }
  ]

  const tabs = {
    defaultActiveKey: "0",
    items: tabsProps
  }

  const tabsHandler = (key: string) => {
    if (key === "Comparison") {
      onCompareChange(true)
    } else {
      onCompareChange(false)
    }
  }

  const resetFields = (value: number) => {
    getOnRangeChangeFunction(value)?.call(undefined, null, ["", ""])
    getOnFuelTypeChangeFunction(value)?.call(undefined, undefined)
    getOnDepotChangeFunction(value)?.call(undefined, undefined)
    getOnCustomerClientChangeFunction(value)?.call(undefined, undefined)
    getOnCustomerCategoryChangeFunction(value)?.call(undefined, undefined)
  }

  const cancelHandler = () => {
    // Сбросить все фильтры на значения по умолчанию
    const defaultOption: AnalyticsSettingsOptions = {
      depotId: undefined,
      fuelTypeId: undefined,
      startDate: undefined,
      endDate: undefined,
      activePeriod: Period.Year,
      customerClientId: undefined,
      customerCategoryId: undefined,
      deliveryType: DeliveryType.All
    }
    
    const clearedOptions = [defaultOption, ...(draftIsComparison ? [{ ...defaultOption }] : [])]
    
    // Применить очищенные фильтры сразу
    setOptionsList(clearedOptions)
    setCompare(false)
    setDeliveryType(DeliveryType.All)
    
    // Обновить draft состояние
    setDraftOptionsList(clearedOptions)
    setDraftCompare(false)
    setDraftDeliveryType(DeliveryType.All)

    if (useDeliveryType && onDeliveryTypeChange) {
      onDeliveryTypeChange(DeliveryType.All)
    }

  }

  return (
    <Flex gap="small" vertical>
      <Space>
        <Tooltip title="Фильтр">
          <Button
            type="default" variant="outlined"
            style={{ borderColor: variables.basecolorlight }}
            icon={<SvgImage src="images/filter-icon.svg" width={16} height={12} />}
            onClick={onOpenFiltersHandler}
          />
        </Tooltip>
      </Space>
      <Drawer
        open={openFilters}
        title="Фильтр"
        width={draftIsComparison ? 860 : 378}
        styles={{ wrapper: { transition: "2s" } }}
        onClose={onCloseFiltersHandler}
        footer={
          <Flex vertical gap="small">
            <Button
            block
            size="large"
            type="primary"
            onClick={submitHandler}
          >
            Применить
          </Button>
            <Button block size="large" color="primary" variant="outlined" onClick={ cancelHandler }>{"Сбросить"}</Button>
          </Flex>
        }
      >
        <Tabs {...tabs} onChange={ tabsHandler} className="customTab" destroyInactiveTabPane={false} />
        {
          useDeliveryType &&
            <Form.Item>
              <Segmented
                options={deliveryTypes}
                value={deliveryType}
                onChange={onDeliveryTypeChange}
              />
            </Form.Item>            
        }
        
        <Flex gap={60}>
          <AnalyticsSettings
            fuelTypes={fuelTypes}
            depots={depots?.response}
            customerClients={clients?.response}
            customerCategories={clientCategories?.response}
            options={draftOptionsList[0]}
            onRangeChange={getOnRangeChangeFunction(1)}
            onPeriodChange={getOnPeriodChangeFunction(1)}
            onChosenFuelTypeChange={getOnFuelTypeChangeFunction(1)}
            onChosenDepotChange={getOnDepotChangeFunction(1)}
            onCustomerClientChange={getOnCustomerClientChangeFunction(1)}
            onCustomerCategoryChange={getOnCustomerCategoryChangeFunction(1)}
            isComparison={draftIsComparison}/>
            {
              draftIsComparison &&
                <Divider className={classes["comparison-options-divider"]} type="vertical"/>
            }
            {
              draftIsComparison &&
                <AnalyticsSettings
                  fuelTypes={fuelTypes}
                  depots={depots?.response}
                  customerClients={clients?.response}
                  customerCategories={clientCategories?.response}
                  options={draftOptionsList[1]}
                  onRangeChange={getOnRangeChangeFunction(2)}
                  onPeriodChange={getOnPeriodChangeFunction(2)}
                  onChosenFuelTypeChange={getOnFuelTypeChangeFunction(2)}
                  onChosenDepotChange={getOnDepotChangeFunction(2)}
                  onCustomerClientChange={getOnCustomerClientChangeFunction(2)}
                  onCustomerCategoryChange={getOnCustomerCategoryChangeFunction(2)}
                  isComparison={draftIsComparison}/>
            }
        </Flex>
      </Drawer>
    </Flex>
  )
}